import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3 sm:rounded-lg" }
const _hoisted_2 = { class: "text-xl font-bold text-gray-900 px-1" }
const _hoisted_3 = { class: "flex flex-col gap-8 bg-white border md:p-6 p-3 border-gray-200 shadow" }
const _hoisted_4 = { class: "hidden lg:table" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("th", { class: "p-1" }, null, -1)
const _hoisted_6 = { class: "p-0.5 pr-3" }
const _hoisted_7 = ["value", "onChange"]
const _hoisted_8 = { class: "p-0.5 pr-3" }
const _hoisted_9 = ["value", "onChange"]
const _hoisted_10 = { class: "p-0.5 pr-3 whitespace-nowrap" }
const _hoisted_11 = ["disabled", "value", "onChange"]
const _hoisted_12 = /*#__PURE__*/_createElementVNode("td", null, null, -1)
const _hoisted_13 = {
  key: 0,
  class: "text-red-900"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "lg:hidden" }
const _hoisted_16 = { class: "grid grid-cols-8 justify-center items-center" }
const _hoisted_17 = /*#__PURE__*/_createElementVNode("div", { class: "p-1 text-xs" }, null, -1)
const _hoisted_18 = { class: "col-span-2 p-1" }
const _hoisted_19 = { class: "col-span-2 p-1" }
const _hoisted_20 = { class: "col-span-2 p-1" }
const _hoisted_21 = /*#__PURE__*/_createElementVNode("div", { class: "p-1" }, null, -1)
const _hoisted_22 = { class: "grid grid-cols-8 gap-2 justify-center items-center" }
const _hoisted_23 = { class: "p-1 text-xs" }
const _hoisted_24 = { class: "col-span-2" }
const _hoisted_25 = ["value", "onChange"]
const _hoisted_26 = { class: "col-span-2" }
const _hoisted_27 = ["value", "onChange"]
const _hoisted_28 = { class: "col-span-2" }
const _hoisted_29 = ["disabled", "value", "onChange"]
const _hoisted_30 = { key: 0 }
const _hoisted_31 = {
  key: 0,
  class: "text-red-900 col-span-8 text-xs"
}
const _hoisted_32 = { class: "flex justify-between flex-wrap gap-2 text-sm" }
const _hoisted_33 = { class: "flex items-center gap-1" }
const _hoisted_34 = { class: "ml-5 list-disc" }
const _hoisted_35 = {
  key: 0,
  class: "text-red-900 text-center text-sm md:text-right"
}

import { useUpdateProjectDurationSettings, durationService } from "@oculai/planner";
import { differenceInMinutes, parse } from "date-fns";
import { Form, SubmissionHandler } from "vee-validate";
import { computed, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate } from "vue-router";
import * as yup from "yup";
import { ObjectSchema } from "yup";
import InfoTooltip from "@/components/UI/InfoTooltip.vue";
import LoadingSpinner from "@/components/UI/LoadingSpinner.vue";
import MainButton from "@/components/UI/MainButton.vue";
import { useSaveBeforeLeaveConfirmationModal } from "@/composables/toast";
import { apiClient } from "@/repositories/clients";
import { DayInterval, ProjectDurationSettings } from "@/types/ProjectDurationSettings";

type FormValues = ProjectDurationSettings["working_hours"];


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkingHours',
  props: {
  projectDurationSettings: {
    type: Object as PropType<ProjectDurationSettings>,
    required: true,
  },
},
  setup(__props) {

const { t, locale } = useI18n();
const localeValue = locale.value;
const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();

const formRef = ref<typeof Form | null>(null);

onBeforeRouteUpdate((to, from, next) => {
  const meta = formRef.value?.meta;

  if (!meta?.dirty) {
    return next();
  }
  showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
    if (confirmed) {
      next();
    }
  });
});

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const props = __props;

const schema = yup.object({
  ...days.reduce((acc, day) => {
    acc[day] = yup
      .object({
        start_time: yup.string().required().nullable(),
        end_time: yup.string().required().nullable(),
        breaks: yup.number().required().nullable(),
      })
      .test(
        "is-start-more-than-end",
        t("admin.durations_project_settings.validation_start_more_than_end"),
        (values) => {
          if (values.start_time && values.end_time) {
            return values.start_time < values.end_time;
          }
          return true;
        },
      )
      .test(
        "is-only-one-empty",
        t("admin.durations_project_settings.validation_start_or_end_empty"),
        (values) =>
          (!!values.start_time && !!values.end_time) ||
          (!values.start_time && !values.end_time && !values.breaks),
      )
      .test(
        "is-breaks-valid",
        t("admin.durations_project_settings.validation_working_hours_breaks"),
        (values) => {
          if (!values.start_time || !values.end_time || !values.breaks) {
            return true;
          }
          const referenceDate = new Date();
          const start = parse(values.start_time, "HH:mm", referenceDate);
          const end = parse(values.end_time, "HH:mm", referenceDate);
          const allMinutes = differenceInMinutes(end, start);
          return values.breaks > 0 && values.breaks * 60 < allMinutes;
        },
      );
    return acc;
  }, {} as Record<string, ObjectSchema<DayInterval>>),
});

const initialValues = computed(() => props.projectDurationSettings.working_hours);

const { updateProjectDurationSettings, isUpdateError } =
  useUpdateProjectDurationSettings(apiClient);

const handleSubmit: SubmissionHandler = (genericObject, context) => {
  return updateProjectDurationSettings({
    working_hours: genericObject as FormValues,
  })
    .then(() => context.resetForm({ values: initialValues.value }))
    .catch(() => {
      // have to catch the error here, otherwise it's propagated as an unhandled error
      // it's already logged by vue-query
      return undefined;
    });
};

const calculateTotalDays = (values: FormValues) =>
  Object.values(values).reduce((acc, day) => acc + durationService.calculateWorkingDay(day), 0);

const calculateTotalHours = (values: FormValues) =>
  Object.values(values).reduce((acc, day) => acc + durationService.calculateWorkingHours(day), 0);

const formatNumber = (value: number) =>
  value.toLocaleString(localeValue, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(t)("admin.durations_project_settings.working_hours_header")), 1),
    _createVNode(_unref(Form), {
      onSubmit: handleSubmit,
      initialValues: initialValues.value,
      validationSchema: _unref(schema),
      class: "flex flex-col gap-3",
      ref_key: "formRef",
      ref: formRef
    }, {
      default: _withCtx(({ isSubmitting, meta, values, setFieldValue, errors }) => [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("table", _hoisted_4, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _hoisted_5,
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (i) => {
                  return _createElementVNode("th", {
                    class: "p-1 timeColumn",
                    key: i
                  }, _toDisplayString(_unref(t)(`calendar.week_days.${i}.short`)), 1)
                }), 64))
              ])
            ]),
            _createElementVNode("tbody", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("td", _hoisted_6, _toDisplayString(_unref(t)("admin.durations_project_settings.start")), 1),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(days, (day) => {
                  return _createElementVNode("td", {
                    class: "p-0.5 timeColumn",
                    key: day
                  }, [
                    _createElementVNode("input", {
                      type: "time",
                      class: _normalizeClass(["oai-inputs", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].start_time,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      start_time: ($event.target as HTMLInputElement).value || null,
                      breaks:
                        ($event.target as HTMLInputElement).value || values[day].end_time
                          ? values[day].breaks
                          : null,
                    })
                  )
                    }, null, 42, _hoisted_7)
                  ])
                }), 64))
              ]),
              _createElementVNode("tr", null, [
                _createElementVNode("td", _hoisted_8, _toDisplayString(_unref(t)("admin.durations_project_settings.end")), 1),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(days, (day) => {
                  return _createElementVNode("td", {
                    class: "p-0.5 timeColumn",
                    key: day
                  }, [
                    _createElementVNode("input", {
                      type: "time",
                      class: _normalizeClass(["oai-inputs", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].end_time,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      end_time: ($event.target as HTMLInputElement).value || null,
                      breaks:
                        ($event.target as HTMLInputElement).value || values[day].start_time
                          ? values[day].breaks
                          : null,
                    })
                  )
                    }, null, 42, _hoisted_9)
                  ])
                }), 64))
              ]),
              _createElementVNode("tr", null, [
                _createElementVNode("td", _hoisted_10, _toDisplayString(_unref(t)("admin.durations_project_settings.breaks")), 1),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(days, (day) => {
                  return _createElementVNode("td", {
                    class: "p-0.5 timeColumn",
                    key: day
                  }, [
                    _createElementVNode("input", {
                      disabled: !values[day].start_time && !values[day].end_time,
                      type: "number",
                      class: _normalizeClass(["oai-inputs disabled:bg-gray-100", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].breaks,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      breaks:
                        parseFloat(($event.target as HTMLInputElement).value.replace(',', '')) ||
                        null,
                    })
                  )
                    }, null, 42, _hoisted_11)
                  ])
                }), 64))
              ]),
              _createElementVNode("tr", null, [
                _hoisted_12,
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(days, (day) => {
                  return _createElementVNode("td", {
                    key: day,
                    class: _normalizeClass(["p-1 text-xs align-top", errors[day] ? '' : 'text-center'])
                  }, [
                    (errors[day])
                      ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(errors[day]), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(`${formatNumber(_unref(durationService).calculateWorkingDay(values[day]))} ${_unref(t)(
                      "admin.durations_project_settings.working_day_label",
                      {
                        count: _unref(durationService).calculateWorkingDay(values[day]),
                      },
                    )}`) + " (" + _toDisplayString(formatNumber(_unref(durationService).calculateWorkingHours(values[day]))) + " " + _toDisplayString(_ctx.$t("admin.durations_project_settings.hours")) + ") ", 1))
                  ], 2)
                }), 64))
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("div", _hoisted_16, [
              _hoisted_17,
              _createElementVNode("div", _hoisted_18, _toDisplayString(_unref(t)("admin.durations_project_settings.start")), 1),
              _createElementVNode("div", _hoisted_19, _toDisplayString(_unref(t)("admin.durations_project_settings.end")), 1),
              _createElementVNode("div", _hoisted_20, _toDisplayString(_unref(t)("admin.durations_project_settings.breaks")), 1),
              _hoisted_21
            ]),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(days, (day, index) => {
              return _createElementVNode("div", {
                class: "my-2",
                key: day
              }, [
                _createElementVNode("div", _hoisted_22, [
                  _createElementVNode("div", _hoisted_23, _toDisplayString(_unref(t)(`calendar.week_days.${index + 1}.short`).charAt(0)), 1),
                  _createElementVNode("div", _hoisted_24, [
                    (_openBlock(), _createElementBlock("input", {
                      key: day,
                      type: "time",
                      class: _normalizeClass(["oai-inputs", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].start_time,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      start_time: ($event.target as HTMLInputElement).value || null,
                      breaks:
                        ($event.target as HTMLInputElement).value || values[day].end_time
                          ? values[day].breaks
                          : null,
                    })
                  )
                    }, null, 42, _hoisted_25))
                  ]),
                  _createElementVNode("div", _hoisted_26, [
                    (_openBlock(), _createElementBlock("input", {
                      key: day,
                      type: "time",
                      class: _normalizeClass(["oai-inputs", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].end_time,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      end_time: ($event.target as HTMLInputElement).value || null,
                      breaks:
                        ($event.target as HTMLInputElement).value || values[day].start_time
                          ? values[day].breaks
                          : null,
                    })
                  )
                    }, null, 42, _hoisted_27))
                  ]),
                  _createElementVNode("div", _hoisted_28, [
                    _createElementVNode("input", {
                      disabled: !values[day].start_time && !values[day].end_time,
                      type: "number",
                      class: _normalizeClass(["oai-inputs disabled:bg-gray-100", errors[day] ? ['border-red-900 focus:border-red-900 text-red-900'] : []]),
                      value: values[day].breaks,
                      onChange: ($event: any) => (
                    setFieldValue(day, {
                      ...values[day],
                      breaks:
                        parseFloat(($event.target as HTMLInputElement).value.replace(',', '')) ||
                        null,
                    })
                  )
                    }, null, 42, _hoisted_29)
                  ]),
                  (_openBlock(), _createElementBlock("div", {
                    key: day,
                    class: _normalizeClass(["p-1 text-xs align-top", errors[day] ? '' : 'text-center'])
                  }, [
                    (!errors[day])
                      ? (_openBlock(), _createElementBlock("span", _hoisted_30, _toDisplayString(`${formatNumber(_unref(durationService).calculateWorkingDay(values[day]))}`), 1))
                      : _createCommentVNode("", true)
                  ], 2)),
                  (errors[day])
                    ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(errors[day]), 1))
                    : _createCommentVNode("", true)
                ])
              ])
            }), 64))
          ])
        ]),
        _createElementVNode("div", _hoisted_32, [
          _createElementVNode("div", null, [
            _createElementVNode("div", _hoisted_33, [
              _createElementVNode("span", null, _toDisplayString(_unref(t)("admin.durations_project_settings.total_working_days_label", {
                total: formatNumber(calculateTotalDays(values as FormValues)),
              })), 1),
              _createVNode(InfoTooltip, { class: "-ml-1" }, {
                text: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)("admin.durations_project_settings.working_days_calculation_tooltip_header")), 1),
                  _createElementVNode("ul", _hoisted_34, [
                    _createElementVNode("li", null, _toDisplayString(_unref(t)("admin.durations_project_settings.working_days_calculation_tooltip_item1")), 1),
                    _createElementVNode("li", null, _toDisplayString(_unref(t)("admin.durations_project_settings.working_days_calculation_tooltip_item2")), 1),
                    _createElementVNode("li", null, _toDisplayString(_unref(t)("admin.durations_project_settings.working_days_calculation_tooltip_item3")), 1)
                  ])
                ]),
                _: 1
              })
            ]),
            _createElementVNode("div", null, _toDisplayString(_unref(t)("admin.durations_project_settings.total_working_hours_label", {
                total: formatNumber(calculateTotalHours(values as FormValues)),
              })), 1)
          ]),
          _createVNode(MainButton, {
            label: _ctx.$t('buttons.save'),
            type: "submit",
            color: "yellow",
            class: "sm:mr-0",
            isDisabled: isSubmitting || !meta.dirty
          }, {
            default: _withCtx(() => [
              (isSubmitting && meta.valid)
                ? (_openBlock(), _createBlock(LoadingSpinner, {
                    key: 0,
                    size: "w-5 h-5",
                    color: "white",
                    class: "mr-2"
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1032, ["label", "isDisabled"])
        ]),
        (_unref(isUpdateError))
          ? (_openBlock(), _createElementBlock("div", _hoisted_35, _toDisplayString(_unref(t)("admin.durations_project_settings.save_error_message")), 1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["initialValues", "validationSchema"])
  ]))
}
}

})